import React, { useState } from 'react';
import cx from 'classnames';
import { PromoteModal } from 'src/components/PromoteModal';
import { ApprovalDecisionModal } from 'src/components/ApprovalDecisionModal';
import { RedeployModal } from 'src/components/RedeployModal';
import { RetryModal } from 'src/components/RetryModal';
import { useGetSageMakerPipelineGraph } from 'src/hooks/useGetSageMakerPipelineGraph';
import { SageMakerPipelineGraph } from 'src/components/SageMakerPipelineGraph';
import { SlideOutPanel } from './SlideOutPanel';
import { OperatorFlowGraph } from './OperatorFlowGraph';
import { useQueryClient } from '@tanstack/react-query';
import { OperatorHelpModal } from 'src/components/OperatorHelpModal';
import { OverrideModal } from 'src/components/OverrideModal';
import { measureTimeSinceAccountPage } from 'src/utils/userMetrics';
import { Button, Tabs } from '@cloudscape-design/components';

export const OperatorFlowVisualizationContainer = ({
  operatorFlowDetails,
  operatorFlowDataLoading,
}: {
  operatorFlowDetails?: OperatorFlowJob;
  operatorFlowDataLoading: boolean;
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState<boolean>(true);

  const [
    pipelineGraphLoading,
    pipelineGraphError,
    pipelineGraphData,
    getPipelineGraph,
  ] = useGetSageMakerPipelineGraph();

  const toggleFullScreen = () =>
    document.fullscreenElement
      ? document.exitFullscreen()
      : document
          .querySelector('.operator-flow-visualization-container')
          ?.requestFullscreen();

  const togglePanel = () => {
    setIsPanelOpen(true);
  };

  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);

  if (!operatorFlowDataLoading && !operatorFlowDetails?.graphDetails) {
    return <p>No graph data available.</p>;
  }
  return (
    <div className="operator-flow-visualization-container">
      <div className="operator-flow-visualization__meta-controls">
        <Button
          loading={loading || operatorFlowDataLoading}
          onClick={() => {
            setLoading(true);
            queryClient.invalidateQueries([
              'operator-flow-data',
              operatorFlowDetails?.id,
            ]);
            // < 100ms request, so delay here to show something happening in the ui
            setTimeout(() => {
              /* istanbul ignore next */
              setLoading(false);
            }, 500);
          }}
        >
          Refresh
        </Button>
      </div>
      <button
        className={cx('operator-flow-visualization-container__overlay', {
          'operator-flow-visualization-container__overlay--hidden':
            !isOverlayVisible,
        })}
        onClick={() => {
          setIsOverlayVisible(false);
          measureTimeSinceAccountPage('overlay-clicked');
        }}
      >
        Click or Tap to Interact
      </button>

      <OperatorFlowGraph
        operatorFlowDetails={operatorFlowDetails}
        getPipelineGraph={getPipelineGraph}
        goFullScreen={toggleFullScreen}
        togglePanel={togglePanel}
      />
      <ApprovalDecisionModal />
      <PromoteModal />
      <RedeployModal />
      <RetryModal />
      <OperatorHelpModal />
      <OverrideModal />

      <SlideOutPanel
        isOpen={isPanelOpen}
        closePanel={() => {
          setIsPanelOpen(false);
        }}
      >
        <Tabs
          tabs={[
            {
              label: 'SageMaker Pipeline Graph',
              id: 'panel-2',
              content: (
                <SageMakerPipelineGraph
                  data={pipelineGraphData}
                  loading={pipelineGraphLoading}
                  error={pipelineGraphError}
                />
              ),
            },
          ]}
        />
      </SlideOutPanel>
    </div>
  );
};
